import { Suspense } from "react";
import { SettingCard } from "@/app/Settings/Settings/Components/SettingFormCard";
import {
  useGqlMutation,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import { useSettingsSupplier } from "@/app/Suppliers/Utils/useSettingsSupplier";
import { supplierIntegrationList } from "@/app/Settings/Settings/GraphQL/integrationQueries";
import { createIntegrationMutation } from "@/app/Settings/Settings/GraphQL/integrationMutations";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";
import { SupplierRecord } from "@/app/Suppliers/GraphQL/supplierRecordQuery";
import { Link } from "@/components/catalyst/link";
import { Badge } from "@/components/catalyst/badge";
import { AlertCircle, Plus } from "lucide-react";
import { integrations } from "@/app/Settings/Integrations/Common/integrations";
import { useVisibleIntegrations } from "@/app/Settings/Integrations/Common/Hooks/useVisibleIntegrations";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { CloudIcon } from "@heroicons/react/20/solid";
import { useTranslations } from "use-intl";
import { useNavigate } from "@tanstack/react-router";

export function SettingsIntegrationsScreen() {
  const { supplier } = useSettingsSupplier();
  const t = useTranslations("settings.integrations");
  if (!supplier) {
    throw new Error("No supplier active");
  }

  return (
    <div className="space-y-6">
      <Suspense
        fallback={
          <SettingCard title={t("title")} description={t("description")}>
            <div className="flex items-center justify-center p-6">
              <Spinner className="size-5" />
            </div>
          </SettingCard>
        }
      >
        <ActiveIntegrationsCard supplier={supplier} />
      </Suspense>
      <AvailableIntegrationsCard supplier={supplier} />
    </div>
  );
}

function ActiveIntegrationsCard({ supplier }: { supplier: SupplierRecord }) {
  const { data } = useSuspenseGqlQuery(supplierIntegrationList, {
    supplier_id: supplier?.id,
  });
  const t = useTranslations("settings.integrations");

  return (
    <SettingCard title={t("title")} description={t("description")}>
      {data.integrations.data.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.integrations.data.map((item) => {
            const integration = integrations.find(
              (integration) => integration.type === item.type,
            );

            const Logo = integration?.logo!;
            const lastError = item.metadata?.last_error as string | undefined;
            const lastErrorAt = item.metadata?.last_error_at as
              | string
              | undefined;

            return (
              <Link
                key={item.id}
                className="relative flex flex-col gap-3 rounded-md border border-gray-100 p-6 hover:bg-gray-50"
                from="/$tenant/$orgId/supplier/{-$supplierId}"
                to="/$tenant/$orgId/supplier/{-$supplierId}/settings/integrations/$integrationId"
                params={(prev) => ({ ...prev, integrationId: item.id })}
                search={(prev) => prev}
              >
                <div className="flex flex-col items-center gap-2 text-center">
                  <Logo className="h-[48px] w-[48px] shrink-0 rounded-md" />
                  <span className="text-sm font-semibold capitalize leading-5 text-gray-900">
                    {integration?.name}
                  </span>
                  <span className="text-xs text-gray-500">
                    {integration?.description}
                  </span>
                </div>
                {lastError ? (
                  <span className="flex items-start gap-1 text-xs text-red-600">
                    <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
                    <span>
                      <span className="font-medium">Last error:</span>{" "}
                      {lastError}
                      {lastErrorAt ? (
                        <span className="ml-1 opacity-70">({lastErrorAt})</span>
                      ) : null}
                    </span>
                  </span>
                ) : null}
                {lastError ? (
                  <div className="mt-auto">
                    <Badge color="red">{t("error")}</Badge>
                  </div>
                ) : null}
              </Link>
            );
          })}
        </div>
      ) : null}

      {data.integrations.data.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6">
          <div className="flex flex-col items-center text-center">
            <h3 className="mb-2 text-base font-medium text-gray-900">
              {t("noActiveIntegrations.title")}
            </h3>
            <p className="max-w-md text-sm text-gray-500">
              {t("noActiveIntegrations.descriptionWithAvailable")}
            </p>
          </div>
        </div>
      ) : null}
    </SettingCard>
  );
}

function AvailableIntegrationsCard({ supplier }: { supplier: SupplierRecord }) {
  const visibleIntegrations = useVisibleIntegrations(supplier.id);
  const navigate = useNavigate();
  const { mutateAsync, isPending } = useGqlMutation(createIntegrationMutation);

  return (
    <SettingCard
      title="Available integrations"
      description="Connect another platform to automate listings, bookings and confirmations."
    >
      {visibleIntegrations.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6">
          <div className="flex flex-col items-center text-center">
            <h3 className="mb-2 text-base font-medium text-gray-900">
              No integrations available
            </h3>
            <p className="max-w-md text-sm text-gray-500">
              There are no integrations available for your organisation. Want us
              to build one? Contact us at{" "}
              <a
                className="font-medium text-blue-600 hover:text-blue-800"
                href="mailto:tradepartners@imoova.com"
              >
                tradepartners@imoova.com
              </a>
              .
            </p>
          </div>
        </div>
      ) : null}
      {visibleIntegrations.length > 0 ? (
        <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {visibleIntegrations.map((integration) => {
            const Logo = integration.logo;
            return (
              <li key={integration.type}>
                <button
                  type="button"
                  disabled={isPending}
                  className="group relative flex w-full flex-col gap-3 rounded-md border border-gray-100 p-6 text-left hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={async () => {
                    const res = await mutateAsync({
                      input: {
                        supplier: { connect: supplier.id },
                        type: integration.type,
                      },
                    });

                    await navigate({
                      from: "/$tenant/$orgId/supplier/{-$supplierId}",
                      to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/integrations/$integrationId",
                      params: (prev) => ({
                        ...prev,
                        integrationId: res.createIntegration.id,
                      }),
                      search: (prev) => prev,
                    });
                  }}
                >
                  <Plus className="absolute right-3 top-3 h-4 w-4 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100" />
                  <div className="flex flex-col items-center gap-2 text-center">
                    <Logo className="size-[48px] shrink-0 rounded-md" />
                    <span className="text-sm font-semibold capitalize leading-5 text-gray-900">
                      {integration.name}
                    </span>
                    <span className="text-xs text-gray-500">
                      {integration.description}
                    </span>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </SettingCard>
  );
}

export function EmptyOutline() {
  return (
    <Empty className="border border-dashed">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <CloudIcon />
        </EmptyMedia>
        <EmptyTitle>Cloud Storage Empty</EmptyTitle>
        <EmptyDescription>
          Upload files to your cloud storage to access them anywhere.
        </EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
}
